<script setup lang="ts">
    import { __ } from '@/js/stores/expressions';
    import SubNavigationLink from '@/js/components/navigation/SubNavigationLink.vue';
    import SubNavigationLinkDropdown from '@/js/components/navigation/SubNavigationLinkDropdown.vue';
    import EchoTooltip from '@/js/components/EchoTooltip.vue';
    import { DatabaseIcon } from '@lucide/vue';
    import { isProd } from '@/config';
</script>
<template>
    <div class="w-full h-full py-6 px-3">
        <nav :aria-label="__('back-office-sub-navigation')">
            <div class="flex gap-2 mb-5 px-3">
                <DatabaseIcon class="size-5 self-start mt-1" />
                <h2 class="heading-md">
                    {{ __('back-office') }}
                </h2>
            </div>
            <ul>
                <li class="mb-2">
                    <SubNavigationLink :to="'/back-office/overview'">
                        {{ __('back-office-overview') }}
                    </SubNavigationLink>
                </li>
                <li class="mb-2">
                    <SubNavigationLink :to="'/back-office/agent-anonymization'">
                        {{ __('back-office-agent-anonymization') }}
                    </SubNavigationLink>
                </li>
                <li class="mb-2">
                    <SubNavigationLinkDropdown :label="__('back-office-general')">
                        <SubNavigationLink :to="'/back-office/data-seeding'">
                            {{ __('back-office-data-seeding') }}
                        </SubNavigationLink>
                        <SubNavigationLink :to="'/back-office/create-tenant'">
                            {{ __('back-office-create-tenant') }}
                        </SubNavigationLink>
                        <SubNavigationLink v-if="!isProd" :to="'/back-office/delete-tenant'">
                            {{ __('back-office-delete-tenant') }}
                        </SubNavigationLink>
                    </SubNavigationLinkDropdown>
                </li>
                <li class="mb-2">
                    <SubNavigationLinkDropdown :label="__('back-office-internal-users')">
                        <SubNavigationLink :to="'/back-office/internal-users/csm'">
                            {{ __('back-office-csm-users') }}
                        </SubNavigationLink>
                        <SubNavigationLink :to="'/back-office/internal-users/sysop'">
                            {{ __('back-office-sysop-users') }}
                        </SubNavigationLink>
                    </SubNavigationLinkDropdown>
                </li>
                <li class="mb-2">
                    <SubNavigationLinkDropdown :label="__('back-office-integrations')">
                        <SubNavigationLink :to="'/back-office/streaming-config'">
                            {{ __('back-office-streaming-config') }}
                        </SubNavigationLink>
                        <EchoTooltip :label="__('back-office-integration-config-deprecated')">
                            <span
                                class="text-body block rounded-sm px-3 py-2 text-gray-medium cursor-not-allowed"
                            >
                                {{ __('back-office-integration-config') }}
                            </span>
                        </EchoTooltip>
                        <SubNavigationLinkDropdown :label="__('back-office-genesys')">
                            <SubNavigationLink :to="'/back-office/genesys-overview'">
                                {{ __('back-office-genesys-tab-overview') }}
                            </SubNavigationLink>
                            <SubNavigationLink :to="'/back-office/genesys-config'">
                                {{ __('back-office-genesys-tab-configure') }}
                            </SubNavigationLink>
                            <SubNavigationLink :to="'/back-office/genesys-sync-data'">
                                {{ __('back-office-genesys-tab-sync') }}
                            </SubNavigationLink>
                        </SubNavigationLinkDropdown>
                    </SubNavigationLinkDropdown>
                </li>
                <li class="mb-2">
                    <SubNavigationLinkDropdown :label="__('back-office-settings')">
                        <SubNavigationLink :to="'/back-office/asr-config'">
                            {{ __('back-office-asr-config') }}
                        </SubNavigationLink>
                        <SubNavigationLink :to="'/back-office/agent-config'">
                            {{ __('back-office-agent-config') }}
                        </SubNavigationLink>
                        <SubNavigationLink :to="'/back-office/categories'">
                            {{ __('back-office-categories') }}
                        </SubNavigationLink>
                        <SubNavigationLink :to="'/back-office/retention-settings'">
                            {{ __('back-office-retention-settings') }}
                        </SubNavigationLink>
                    </SubNavigationLinkDropdown>
                </li>
                <li class="mb-2">
                    <SubNavigationLinkDropdown :label="__('back-office-jobs')">
                        <SubNavigationLink :to="'/back-office/asr-enqueue'">
                            {{ __('back-office-asr-analysis-enqueue') }}
                        </SubNavigationLink>
                    </SubNavigationLinkDropdown>
                </li>
                <li class="mb-2">
                    <SubNavigationLinkDropdown :label="__('back-office-v1')">
                        <SubNavigationLink :to="'/back-office/migrate-legacy-tenant'">
                            {{ __('back-office-install-customer-from-v1') }}
                        </SubNavigationLink>
                        <SubNavigationLink :to="'/back-office/migrate-legacy-integration'">
                            {{ __('back-office-install-integration-from-v1') }}
                        </SubNavigationLink>
                        <SubNavigationLink :to="'/back-office/key-indicate-sync-settings'">
                            {{ __('back-office-key-indicate-sync-settings') }}
                        </SubNavigationLink>
                        <SubNavigationLink :to="'/back-office/import-tenant-config'">
                            {{ __('back-office-import-tenant-config') }}
                        </SubNavigationLink>
                        <SubNavigationLink :to="'/back-office/update-org-tree'">
                            {{ __('back-office-update-org-tree') }}
                        </SubNavigationLink>
                        <SubNavigationLink :to="'/back-office/import-agents'">
                            {{ __('back-office-import-agents-from-v1') }}
                        </SubNavigationLink>
                        <SubNavigationLink :to="'/back-office/import-integration-config'">
                            {{ __('back-office-import-integration-config') }}
                        </SubNavigationLink>
                        <SubNavigationLink :to="'/back-office/sync-raw-data'">
                            {{ __('back-office-sync-raw-data') }}
                        </SubNavigationLink>
                        <SubNavigationLink :to="'/back-office/sync-coaching-data'">
                            {{ __('back-office-sync-coaching-data') }}
                        </SubNavigationLink>
                        <SubNavigationLink :to="'/back-office/sync-ai-insights-data'">
                            {{ __('back-office-sync-ai-insights-data') }}
                        </SubNavigationLink>
                    </SubNavigationLinkDropdown>
                </li>
            </ul>
            <hr class="my-4 border-gray-lightest" />
            <ul>
                <li class="mb-2">
                    <SubNavigationLinkDropdown :label="__('back-office-development')">
                        <SubNavigationLink :to="'/back-office/backfill-analysis-received-at'">
                            {{ __('back-office-backfill-analysis-received-at') }}
                        </SubNavigationLink>
                    </SubNavigationLinkDropdown>
                </li>
            </ul>
        </nav>
    </div>
</template>
